import React from 'react';
import { GetStaticProps } from 'next';
import Head from 'next/head';
import Link from 'next/link';
import Image from 'next/image';
import { useQuery } from '@apollo/client';
import client from '../lib/apollo';
import { GET_POSTS, GET_SITE_INFO } from '../lib/queries';
import Header from '../components/Header';

interface Post {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  date: string;
  featuredImage?: {
    node: {
      sourceUrl: string;
      altText: string;
    };
  };
  author: {
    node: {
      name: string;
      slug: string;
    };
  };
  categories: {
    nodes: Array<{
      name: string;
      slug: string;
    }>;
  };
}

interface SiteInfo {
  title: string;
  description: string;
  url: string;
}

interface HomePageProps {
  posts: Post[];
  siteInfo: SiteInfo;
}

export default function HomePage({ posts, siteInfo }: HomePageProps) {
  const phoneNumber = "(630) 715-8829";
  const companyName = "Partners Plumbing Co.";
  const serviceArea = "Kane County, Illinois";
  const address = "Kane County, IL 60177";
  const email = "info@partnersinplumbing.com";

  return (
    <>
      <Head>
        <title>{`Plumber Near Me in Geneva, IL | Emergency Plumber Kane County | ${companyName}`}</title>
        <meta name="description" content={`#1 Plumber near me in Geneva, St Charles & Kane County, IL. Emergency plumber, drain cleaning, water heater repair. Licensed plumbing services. Call ${phoneNumber} now!`} />
        <meta name="keywords" content="plumber near me, emergency plumber near me, plumber Kane County IL, emergency plumber Geneva IL, plumber St Charles IL, local plumber near me, drain cleaning near me, water heater repair near me, plumbing services near me" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href="https://partnersinplumbing.com/" />
        <link rel="icon" href="/favicon.ico" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Plumber",
            "name": companyName,
            "description": "Licensed plumber providing emergency plumbing services, drain cleaning, water heater repair, and all plumbing services in Kane County, Geneva, St Charles, and South Elgin, IL",
            "url": "https://partnersinplumbing.com",
            "telephone": phoneNumber,
            "email": email,
            "image": "https://partnersinplumbing.com/images/optimized/logo-desktop.webp",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "",
              "addressLocality": "Geneva",
              "addressRegion": "IL",
              "postalCode": "60177",
              "addressCountry": "US"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": 41.8875,
              "longitude": -88.3053
            },
            "openingHoursSpecification": [
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                "opens": "08:00",
                "closes": "18:00"
              },
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": "Saturday",
                "opens": "09:00",
                "closes": "16:00"
              }
            ],
            "priceRange": "$$",
            "areaServed": [
              {
                "@type": "City",
                "name": "Geneva",
                "address": { "@type": "PostalAddress", "addressRegion": "IL", "addressCountry": "US" }
              },
              {
                "@type": "City",
                "name": "St Charles",
                "address": { "@type": "PostalAddress", "addressRegion": "IL", "addressCountry": "US" }
              },
              {
                "@type": "City",
                "name": "South Elgin",
                "address": { "@type": "PostalAddress", "addressRegion": "IL", "addressCountry": "US" }
              },
              {
                "@type": "AdministrativeArea",
                "name": "Kane County",
                "address": { "@type": "PostalAddress", "addressRegion": "IL", "addressCountry": "US" }
              }
            ],
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Plumbing Services",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Emergency Plumbing Service",
                    "description": "Emergency plumber for burst pipes, water leaks, and plumbing emergencies"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Drain Cleaning",
                    "description": "Professional drain cleaning and clogged drain repair services"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Water Heater Repair",
                    "description": "Water heater repair and replacement services"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Pipe Repair",
                    "description": "Broken pipe repair and pipe leak repair"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Toilet Repair",
                    "description": "Toilet installation and toilet repair services"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Sewer Repair",
                    "description": "Sewer line repair and sewer cleaning services"
                  }
                }
              ]
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "500"
            }
          })}
        </script>
      </Head>

      <div className="min-h-screen bg-white font-inter">
        <Header currentPage="/" phoneNumber={phoneNumber} companyName={companyName} />

        {/* Hero Section */}
        <section className="hero-section">
          <div className="container relative">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div className="text-center lg:text-left">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 lg:mb-6 leading-tight">
                  Professional Plumber in Geneva, IL - Serving Kane County, St Charles & South Elgin
                </h1>
                <p className="text-lg sm:text-xl mb-6 lg:mb-8 text-slate-100 leading-relaxed">
                  Local plumber near me providing <Link href="/services/emergency-plumbing-service" className="text-yellow-400 hover:text-yellow-300 font-semibold underline">emergency plumbing services</Link>, drain cleaning, and water heater repair throughout Kane County, Geneva, St Charles, and South Elgin, IL. Licensed, insured, and available for plumbing emergencies.
                </p>
                <div className="mb-6 lg:mb-8">
                  <a href={`tel:${phoneNumber}`} className="phone-number">
                    {phoneNumber}
                  </a>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                  <Link href="/contact" className="btn-primary text-center">
                    Get Free Quote
                  </Link>
                  <a href={`tel:${phoneNumber}`} className="bg-transparent border-2 border-white text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-bold text-base sm:text-lg hover:bg-white hover:text-slate-900 transition-all duration-200 text-center">
                    Call Now
                  </a>
                </div>
              </div>
              <div className="hidden lg:block">
                <div className="bg-white/10 p-8 rounded-xl backdrop-blur-sm border border-white/20 shadow-2xl">
                  <h3 className="text-2xl font-bold mb-4"><Link href="/services/emergency-plumbing-service" className="text-white hover:text-yellow-400 transition-colors">Emergency Service Available</Link></h3>
                  <p className="text-slate-100 mb-4 leading-relaxed">Professional <Link href="/services/emergency-plumbing-service" className="text-yellow-400 hover:text-yellow-300 font-semibold underline">emergency plumber in Kane County</Link> with reliable response times</p>
                  <div className="flex items-center text-yellow-400 mb-4">
                    <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center mr-3">
                      <div className="w-4 h-4 bg-yellow-600 rounded-full"></div>
                    </div>
                    <span className="font-semibold">Same-Day Service</span>
                  </div>
                  <div className="flex items-center text-yellow-400">
                    <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center mr-3">
                      <div className="w-4 h-4 bg-yellow-600 rounded-full"></div>
                    </div>
                    <span className="font-semibold">Licensed & Experienced</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Indicators */}
        <section className="py-8 bg-gray-100 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mb-2">
                  <div className="w-6 h-6 bg-white rounded-full"></div>
                </div>
                <span className="text-sm font-semibold text-gray-800">Licensed & Insured</span>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mb-2">
                  <div className="w-6 h-6 bg-white rounded-full"></div>
                </div>
                <span className="text-sm font-semibold text-gray-800">5000+ Customers</span>
              </div>
            </div>
          </div>
        </section>

        {/* Key Service Areas */}
        <section className="py-12 md:py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Expert Plumbing Services Near Me in Kane County</h2>
              <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">Your local plumber near me for all residential and commercial plumbing needs in Geneva, St Charles, South Elgin, and throughout Kane County, Illinois</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {[
                {
                  title: "Broken Pipe Repair",
                  description: "Emergency broken pipe repair and replacement services throughout Kane County with rapid response times",
                  icon: "broken-pipe",
                  features: ["Pipe replacement", "Leak detection", "Insurance support", "Emergency response"],
                  link: "/services/broken-pipe"
                },
                {
                  title: "Leaky Pipe, Sink & Toilet Repair",
                  description: "Professional leak repair for pipes, sinks, and toilets throughout Kane County, Geneva, Saint Charles, and South Elgin",
                  icon: "leak",
                  features: ["Pipe leak repair", "Sink leak fixes", "Toilet leak repair", "Same-day service"],
                  link: "/services/leaky-plumbing"
                },
                {
                  title: "Water Heater Replacement",
                  description: "Expert water heater replacement including tank, tankless, and hybrid systems with energy-efficient options",
                  icon: "water-heater",
                  features: ["Tank & tankless units", "Energy efficient models", "Same-day installation", "10-year warranties"],
                  link: "/services/water-heater-replacement"
                },
                {
                  title: "Reverse Osmosis Installation",
                  description: "Professional reverse osmosis system installation for pure, clean drinking water in your home",
                  icon: "reverse-osmosis",
                  features: ["Pure drinking water", "Professional installation", "System maintenance", "Quality filtration"],
                  link: "/services/reverse-osmosis"
                },
                {
                  title: "Toilet Installation & Replacement",
                  description: "Complete toilet installation and replacement services with same-day availability",
                  icon: "toilet",
                  features: ["New toilet installation", "Toilet replacement", "Same-day service", "All brands serviced"],
                  link: "/services/toilet-installation"
                },
                {
                  title: "Emergency Water Cleanup",
                  description: "Emergency water cleanup and damage restoration services throughout Kane County",
                  icon: "water-cleanup",
                  features: ["Water extraction", "Flood cleanup", "Damage assessment", "Emergency response"],
                  link: "/services/water-cleanup"
                },
                {
                  title: "Emergency Plumbing Service",
                  description: "Emergency plumbing services for burst pipes, major leaks, and urgent plumbing issues",
                  icon: "emergency",
                  features: ["Emergency availability", "Burst pipe repair", "Emergency repairs", "Fast response"],
                  link: "/services/emergency-plumbing-service"
                }
              ].map((service, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 md:p-8 hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-slate-300 flex flex-col h-full">
                  <div className="text-center flex-grow">
                    <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                      <div className="w-8 h-8 bg-white rounded-full"></div>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-3 leading-tight">{service.title}</h3>
                    <p className="text-gray-600 leading-relaxed text-sm sm:text-base mb-4">{service.description}</p>
                    
                    <ul className="text-left space-y-2 mb-4">
                      {service.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center text-sm text-gray-600">
                          <span className="text-green-500 mr-2">✓</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="border-t border-gray-100 pt-6 mt-6 space-y-3">
                    <Link href={service.link} className="block w-full bg-slate-800 text-white text-center px-6 py-3 rounded-lg font-semibold hover:bg-slate-900 transition-all duration-200 shadow-md hover:shadow-lg text-sm sm:text-base">
                      Learn More
                    </Link>
                    <Link href="/contact" className="block w-full bg-gray-100 text-slate-800 text-center px-6 py-2 rounded-lg font-medium hover:bg-gray-200 transition-all duration-200 text-sm">
                      Get Free Quote
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Customer Testimonials */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">What Our Customers Say</h2>
              <p className="text-xl text-gray-600">Over 5,000 satisfied customers throughout Kane County</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  name: "Sarah M.",
                  location: "Geneva, IL",
                  review: "Partners Plumbing saved the day when our water heater failed on a Friday night. They came out within an hour and had us back up and running. Professional, honest, and reasonably priced.",
                  rating: 5,
                  service: "Emergency Water Heater Repair"
                },
                {
                  name: "Mike R.",
                  location: "Saint Charles, IL",
                  review: "I've used Partners Plumbing for several jobs over the years - drain cleaning, faucet repairs, and a full bathroom renovation. They're always on time, do quality work, and clean up after themselves.",
                  rating: 5,
                  service: "Multiple Services"
                },
                {
                  name: "Jennifer L.",
                  location: "South Elgin, IL",
                  review: "Had a sewer line backup that was causing flooding in our basement. Partners Plumbing responded immediately and handled everything professionally. They even worked with our insurance company. Highly recommend!",
                  rating: 5,
                  service: "Emergency Sewer Line Repair"
                }
              ].map((testimonial, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-6 shadow-md">
                  <div className="flex items-center mb-4">
                    <div className="flex text-yellow-400">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <span key={i} className="text-xl">★</span>
                      ))}
                    </div>
                    <span className="ml-2 text-gray-600">({testimonial.rating}/5)</span>
                  </div>
                  <p className="text-gray-700 mb-4 italic">"{testimonial.review}"</p>
                  <div className="border-t pt-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-semibold text-gray-900">{testimonial.name}</p>
                        <p className="text-sm text-gray-600">{testimonial.location}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Service:</p>
                        <p className="text-sm font-medium text-slate-800">{testimonial.service}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

          </div>
        </section>

        {/* Unique Value Propositions */}
        <section className="py-20 bg-slate-800 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-black opacity-10"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4">Why Choose Our Licensed Plumber in Geneva, IL & Kane County</h2>
              <p className="text-xl text-slate-100 max-w-3xl mx-auto">The best plumber near me in Kane County - Licensed plumbing contractor serving Geneva, St Charles, South Elgin with emergency plumbing services</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  title: "Licensed & Fully Insured",
                  description: "Illinois State licensed plumbers with comprehensive general liability and workers' compensation insurance",
                  icon: "license"
                },
                {
                  title: "100% Satisfaction Guarantee",
                  description: "We stand behind our work with a complete satisfaction guarantee on all services. If you're not happy, we'll make it right.",
                  icon: "guarantee"
                },
                {
                  title: "Transparent, Upfront Pricing",
                  description: "No hidden costs or surprise fees. You'll know the exact price before we start any work, with written estimates provided.",
                  icon: "pricing"
                },
                {
                  title: "Fully Equipped Service Vehicles",
                  description: "Our mobile workshops carry over 1,000 parts, allowing us to complete most repairs in one visit without return trips.",
                  icon: "vehicle"
                },
                {
                  title: "Emergency Service",
                  description: "Professional emergency plumber in Kane County providing fast response for burst pipes, water leaks, and all plumbing emergencies 24/7.",
                  icon: "emergency-service"
                },
                {
                  title: "Local Kane County Experts",
                  description: "Deep knowledge of local plumbing codes, common issues in Kane County homes, and established relationships with local suppliers.",
                  icon: "local"
                }
              ].map((value, index) => (
                <div key={index} className="text-center bg-white/10 p-6 rounded-xl backdrop-blur-sm border border-white/20">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <div className="w-6 h-6 bg-white rounded-full"></div>
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{value.title}</h3>
                  <p className="text-slate-100 leading-relaxed">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Plumber Near Me - Service Areas in Kane County & Beyond</h2>
              <p className="text-xl text-gray-600">Local plumber serving Kane County, IL - Emergency plumber near me available in Geneva, St Charles, South Elgin & surrounding communities</p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 text-center">
              {[
                { name: "Plumber Kane County IL", shortName: "Kane County" },
                { name: "Plumber Geneva IL", shortName: "Geneva" },
                { name: "Plumber St Charles IL", shortName: "St Charles" },
                { name: "Plumber South Elgin IL", shortName: "South Elgin" }
              ].map((area, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                  <span className="font-semibold text-gray-900 block mb-1">{area.shortName}</span>
                  <span className="text-sm text-gray-600">Emergency Plumber</span>
                </div>
              ))}
            </div>
            
            <div className="text-center mt-8 mb-8">
              <p className="text-gray-700 mb-4 font-medium">Searching for "plumber near me" in Kane County? We provide:</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto text-left">
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-slate-800 mb-2">Emergency Plumber</h3>
                  <p className="text-sm text-gray-600">Emergency plumber near me available in Kane County</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-slate-800 mb-2">Drain Cleaning Near Me</h3>
                  <p className="text-sm text-gray-600">Professional drain cleaning services throughout Geneva & St Charles</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-slate-800 mb-2">Water Heater Repair</h3>
                  <p className="text-sm text-gray-600">Fast water heater repair near me in Kane County, IL</p>
                </div>
              </div>
            </div>
            
            <div className="text-center">
              <p className="text-gray-600">Don't see your area? <a href={`tel:${phoneNumber}`} className="text-slate-800 hover:text-slate-900 font-medium">Call your local plumber at {phoneNumber}</a> - we may still be able to help!</p>
            </div>
          </div>
        </section>

        {/* Recent Blog Posts */}
        {posts.length > 0 && (
          <section className="py-16 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">Plumbing Tips & Insights</h2>
                <p className="text-xl text-gray-600">Expert advice from our professional plumbers</p>
              </div>
              <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                {posts.slice(0, 3).map((post) => (
                  <article key={post.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                    {post.featuredImage && (
                      <div className="aspect-w-16 aspect-h-9">
                        <Image
                          src={post.featuredImage.node.sourceUrl}
                          alt={post.featuredImage.node.altText || post.title}
                          width={400}
                          height={225}
                          className="w-full h-48 object-cover"
                          loading="lazy"
                          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                          quality={75}
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-center text-sm text-gray-500 mb-2">
                        <time dateTime={post.date}>
                          {new Date(post.date).toLocaleDateString()}
                        </time>
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">
                        <Link href={`/posts/${post.slug}`} className="hover:text-slate-900 transition-colors">
                          {post.title}
                        </Link>
                      </h3>
                      <div className="text-gray-600 mb-4 line-clamp-3" dangerouslySetInnerHTML={{ __html: post.excerpt }} />
                      <Link href={`/posts/${post.slug}`} className="text-slate-800 hover:text-slate-900 font-medium">
                        Read More →
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Map Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Service Area</h2>
              <p className="text-xl text-gray-600">Serving Kane County and surrounding communities with professional plumbing services</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <iframe
                  src={`https://www.google.com/maps/embed/v1/view?key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY}&center=41.9406,-88.5300&zoom=10&maptype=roadmap`}
                  width="100%"
                  height="450"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Partners Plumbing Co. Service Area"
                  className="w-full h-64 sm:h-60 md:h-64"
                ></iframe>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Service Area</h3>
                    <address className="text-slate-800 font-bold not-italic">
                      Kane County, IL 60177
                    </address>
                    <p className="text-gray-600 mt-2">Serving Kane County and surrounding areas with professional plumbing services</p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Business Hours</h3>
                    <div className="space-y-1 text-gray-600">
                      <div className="flex justify-between">
                        <span>Monday - Friday:</span>
                        <span className="font-medium">8:00 AM - 6:00 PM</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Saturday:</span>
                        <span className="font-medium">9:00 AM - 4:00 PM</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Sunday:</span>
                        <span className="font-medium">Emergency Only</span>
                      </div>
                      <div className="flex justify-between font-semibold text-red-600">
                        <span>Emergency Service:</span>
                        <span>Available</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-800 text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h3 className="text-xl font-bold mb-4">{companyName}</h3>
                <p className="text-gray-300 mb-4">Professional plumbing services you can trust. Licensed, insured, and reliable serving Kane County.</p>
                <a href={`tel:${phoneNumber}`} className="text-yellow-400 font-bold text-lg hover:text-yellow-300 block mb-2">
                  {phoneNumber}
                </a>
                <a href={`mailto:${email}`} className="text-gray-300 hover:text-white block mb-2">
                  {email}
                </a>
                <p className="text-gray-300 text-sm">
                  {address}
                </p>
              </div>
              <div>
                <h4 className="text-lg font-semibold mb-4">Services</h4>
                <ul className="space-y-2 text-gray-300">
                  <li><Link href="/services/broken-pipe" className="hover:text-white">Broken Pipe Repair</Link></li>
                  <li><Link href="/services/leaky-plumbing" className="hover:text-white">Leak Repair</Link></li>
                  <li><Link href="/services/water-heater-replacement" className="hover:text-white">Water Heater Replacement</Link></li>
                  <li><Link href="/services/reverse-osmosis" className="hover:text-white">Reverse Osmosis</Link></li>
                  <li><Link href="/services/toilet-installation" className="hover:text-white">Toilet Installation</Link></li>
                  <li><Link href="/services/emergency-plumbing-service" className="hover:text-white">Emergency Plumbing</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="text-lg font-semibold mb-4">Company</h4>
                <ul className="space-y-2 text-gray-300">
                  <li><Link href="/about" className="hover:text-white">About Us</Link></li>
                  <li><Link href="/blog" className="hover:text-white">Blog</Link></li>
                  <li><Link href="/contact" className="hover:text-white">Contact</Link></li>
                  <li><Link href="/services" className="hover:text-white">All Services</Link></li>
                </ul>
              </div>
            </div>
            <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
              <p>&copy; 2024 {companyName}. All rights reserved. | Licensed & Insured | Emergency Service Available</p>
              <p className="mt-2 text-sm">Licensed Plumbing Contractor | Serving Kane County</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}

export const getStaticProps: GetStaticProps = async () => {
  try {
    // Check if WordPress URL is configured
    if (!process.env.NEXT_PUBLIC_WORDPRESS_URL || process.env.NEXT_PUBLIC_WORDPRESS_URL.includes('your-wordpress-site.com')) {
      console.log('WordPress URL not configured, building with static data');
      return {
        props: {
          posts: [],
          siteInfo: {
            title: 'Partners Plumbing Co.',
            description: 'Professional plumbing services you can trust',
            url: '',
          },
        },
        revalidate: 60,
      };
    }

    const [postsResult, siteInfoResult] = await Promise.all([
      client.query({
        query: GET_POSTS,
        variables: { first: 6 },
      }),
      client.query({
        query: GET_SITE_INFO,
      }),
    ]);

    return {
      props: {
        posts: postsResult.data.posts.nodes || [],
        siteInfo: siteInfoResult.data.generalSettings || {
          title: 'Partners Plumbing Co.',
          description: 'Professional plumbing services you can trust',
          url: '',
        },
      },
      revalidate: 30, // Reduced from 60 seconds for faster updates
    };
  } catch (error) {
    console.error('Error fetching data:', error);
    
    return {
      props: {
        posts: [],
        siteInfo: {
          title: 'Partners Plumbing Co.',
          description: 'Professional plumbing services you can trust',
          url: '',
        },
      },
      revalidate: 30, // Reduced from 60 seconds for faster updates
    };
  }
}; 